/**
 * Dashboard di riepilogo.
 *
 * Nota sul multi-valuta: i totali sono raggruppati per valuta e NON sommati tra
 * loro finché `fx_rates` non è alimentata (F-020). Sommare EUR e USD darebbe un
 * numero privo di significato, quindi la UI lo dichiara esplicitamente.
 */

import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import {
  PORTFOLIO_TYPE_LABELS,
  TX_TYPE_LABELS,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatQuantity,
  labelFor,
  signOf,
} from '@/lib/format';
import { useAuth } from '@/features/auth/auth-context';
import { Alert, Badge, Card, EmptyState, Spinner, StatCard, TableWrap } from '@/ui';
import { useDashboardSummary, usePortfolios } from './api';
import type { AllocationSlice, CurrencyTotal, PortfolioSummary } from './api';
import './dashboard.css';

/** Solo per la larghezza delle barre: un ausilio visivo, non un calcolo (ADR-0006). */
function toWidthNumber(value: string | null | undefined): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

/** Decide solo se mostrare una riga o una scomposizione, non un importo. */
function isNonZero(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric !== 0;
}

/**
 * Una barra a due tonalità: titoli nel colore pieno, liquidità nello stesso
 * colore attenuato. Le larghezze sono relative a `max`, cioè alla riga più
 * grande della stessa valuta.
 */
function StackedBar({
  invested,
  cash,
  max,
  color,
  className,
}: {
  invested: string;
  cash: string;
  max: number;
  color: string;
  className: string;
}) {
  const investedWidth = max > 0 ? (toWidthNumber(invested) / max) * 100 : 0;
  const cashWidth = max > 0 ? (toWidthNumber(cash) / max) * 100 : 0;
  return (
    <span className={className} aria-hidden="true">
      <span className="alloc__fill" style={{ width: `${investedWidth}%`, background: color }} />
      <span className="alloc__fill alloc__fill--cash" style={{ width: `${cashWidth}%`, background: color }} />
    </span>
  );
}

/** Sotto il totale, «titoli X · liquidità Y»: solo quando c'è qualcosa da scomporre. */
function Breakdown({ invested, cash, currency }: { invested: string; cash: string; currency: string }) {
  if (!isNonZero(cash) || !isNonZero(invested)) return null;
  return (
    <span className="alloc__breakdown">
      titoli {formatCurrency(invested, currency)} · liquidità {formatCurrency(cash, currency)}
    </span>
  );
}

type AllocationRow = {
  key: string;
  label: string;
  currency: string;
  /** Titoli: la tonalità piena. */
  invested: string;
  /** Liquidità: la tonalità attenuata. */
  cash: string;
  /** Ciò che si legge nella colonna del valore. */
  total: string;
  color: string;
};

function AllocationBars({ rows }: { rows: AllocationRow[] }) {
  if (rows.length === 0) {
    return <p className="text-muted">Nessun dato di ripartizione disponibile.</p>;
  }

  // Le percentuali sono relative alla riga più grande DELLA STESSA VALUTA:
  // la barra è un ausilio visivo, non un calcolo su cui basare decisioni.
  const maxByCurrency = new Map<string, number>();
  for (const row of rows) {
    const value = toWidthNumber(row.invested) + toWidthNumber(row.cash);
    const current = maxByCurrency.get(row.currency) ?? 0;
    if (value > current) maxByCurrency.set(row.currency, value);
  }
  const mixedCurrencies = rows.some((r) => r.currency !== rows[0]?.currency);

  return (
    <ul className="alloc">
      {rows.map((row) => (
        <li className="alloc__row" key={`${row.key}-${row.currency}`}>
          <span className="alloc__label">
            {row.label}
            {mixedCurrencies && <Badge>{row.currency}</Badge>}
          </span>
          <StackedBar
            className="alloc__track"
            invested={row.invested}
            cash={row.cash}
            max={maxByCurrency.get(row.currency) ?? 0}
            color={row.color}
          />
          <span className="alloc__value numeric">
            {formatCurrency(row.total, row.currency)}
            <Breakdown invested={row.invested} cash={row.cash} currency={row.currency} />
          </span>
        </li>
      ))}
    </ul>
  );
}

const CASH_COLOR = 'var(--color-text-muted)';

/**
 * Per tipologia: una barra per tipo (solo titoli) e, in fondo, una riga
 * dedicata alla liquidità di tutti i portafogli. La liquidità non è «di un
 * tipo»: è denaro fermo, e lo si vuole vedere come tale (TODO.md, 15/09/2026).
 */
function typeRows(slices: AllocationSlice[], totals: CurrencyTotal[]): AllocationRow[] {
  const rows: AllocationRow[] = slices.map((slice, index) => ({
    key: slice.key,
    label: labelFor(PORTFOLIO_TYPE_LABELS, slice.label),
    currency: slice.currency,
    invested: slice.totalValue,
    cash: '0',
    total: slice.totalValue,
    color: `var(--color-cat-${(index % 6) + 1})`,
  }));
  for (const total of totals) {
    if (!isNonZero(total.cashBalance)) continue;
    rows.push({
      key: 'liquidity',
      // «nei portafogli», e non «Liquidità» secco: un portafoglio di TIPO `cash`
      // si chiama già così, e due righe omonime si leggerebbero come un doppione.
      label: 'Liquidità nei portafogli',
      currency: total.currency,
      invested: '0',
      cash: total.cashBalance,
      total: total.cashBalance,
      color: CASH_COLOR,
    });
  }
  return rows;
}

/** Per piattaforma: titoli e liquidità sulla stessa riga, in due tonalità. */
function platformRows(slices: AllocationSlice[]): AllocationRow[] {
  return slices.map((slice, index) => ({
    key: slice.key,
    label: slice.label,
    currency: slice.currency,
    invested: slice.totalValue,
    cash: slice.cashBalance,
    total: slice.totalWithCash,
    color: `var(--color-cat-${(index % 6) + 1})`,
  }));
}

/**
 * La colonna del valore nella tabella dei portafogli: il totale con liquidità,
 * la scomposizione, e la stessa barra a due tonalità delle ripartizioni. Senza
 * snapshot non c'è un valore dei titoli: si mostra la sola liquidità.
 */
function PortfolioValueCell({ portfolio, max }: { portfolio: PortfolioSummary; max: number }) {
  const currency = portfolio.latestSnapshot?.currency ?? portfolio.baseCurrency;
  const invested = portfolio.latestSnapshot?.totalValue ?? '0';
  const cash = portfolio.cashBalance ?? '0';
  const total = portfolio.totalWithCash ?? (portfolio.latestSnapshot ? portfolio.latestSnapshot.totalValue : cash);
  return (
    <span className="dash__value-stack">
      <span>{formatCurrency(total, currency)}</span>
      <Breakdown invested={invested} cash={cash} currency={currency} />
      <StackedBar
        className="alloc__track dash__mini-track"
        invested={invested}
        cash={cash}
        max={max}
        color="var(--color-cat-1)"
      />
    </span>
  );
}

/**
 * Perché il patrimonio considera meno portafogli di quanti ne esistono. Va detto
 * per intero e distinguendo le due cause: un utente che ne vede tre e ne conta
 * due nel totale, senza spiegazione, cerca un bug che non c'è — ed è la stessa
 * ragione per cui `simulatedPortfolios` era già dichiarato.
 */
function excludedHint(counts: { portfolios: number; simulatedPortfolios: number; demoPortfolios: number }): string {
  const reasons = [
    counts.simulatedPortfolios > 0 ? `${counts.simulatedPortfolios} simulati` : null,
    counts.demoPortfolios > 0 ? `${counts.demoPortfolios} su conto demo del broker` : null,
  ].filter(Boolean);

  if (reasons.length === 0) return `${counts.portfolios} in totale`;
  return `${counts.portfolios} in totale, di cui ${reasons.join(' e ')} (esclusi dai totali: contengono denaro finto)`;
}

export function DashboardPage() {
  const { user } = useAuth();
  const summaryQuery = useDashboardSummary();
  const portfoliosQuery = usePortfolios();

  if (summaryQuery.isPending) {
    return <Spinner label="Caricamento della dashboard…" />;
  }

  if (summaryQuery.isError) {
    const error = summaryQuery.error;
    return (
      <Alert variant="error" title="Impossibile caricare la dashboard">
        {error instanceof ApiError ? error.message : 'Errore imprevisto.'}
      </Alert>
    );
  }

  const summary = summaryQuery.data;
  const portfolios = portfoliosQuery.data?.items ?? [];
  const hasData = summary.counts.portfolios > 0;

  // La barra di ogni portafoglio è relativa al più grande della stessa valuta.
  const maxPortfolioValue = new Map<string, number>();
  for (const portfolio of portfolios) {
    const currency = portfolio.latestSnapshot?.currency ?? portfolio.baseCurrency;
    const value = toWidthNumber(portfolio.latestSnapshot?.totalValue) + toWidthNumber(portfolio.cashBalance);
    if (value > (maxPortfolioValue.get(currency) ?? 0)) maxPortfolioValue.set(currency, value);
  }

  return (
    <div className="dash">
      <header className="dash__header">
        <div>
          <h1 className="dash__title">
            Ciao{user?.displayName ? `, ${user.displayName}` : ''} 👋
          </h1>
          <p className="dash__subtitle">
            {summary.lastSnapshotDate
              ? `Ultimo snapshot: ${formatDate(summary.lastSnapshotDate)}`
              : 'Nessuno snapshot ancora importato'}
          </p>
        </div>
      </header>

      {!hasData ? (
        <Card>
          <EmptyState
            icon="🚀"
            title="Nessun portafoglio ancora"
            description={
              <>
                LabInvestment si popola importando i file che esporti dalle tue piattaforme:
                l'estratto del patrimonio in PDF, la cronologia operazioni in CSV, o entrambi.
                Portafogli e strumenti vengono creati automaticamente.
              </>
            }
            action={
              <Link className="btn btn--primary" to="/imports">
                Importa i tuoi dati
              </Link>
            }
          />
        </Card>
      ) : (
        <>
          {summary.hasMixedCurrencies && (
            <Alert variant="warning" title="Portafogli in valute diverse">
              I totali sono mostrati separatamente per valuta. La conversione verso la valuta
              base richiede i tassi di cambio, previsti dalla feature F-020.
            </Alert>
          )}

          <section className="dash__stats">
            {summary.totalsByCurrency.map((total) => (
              <StatCard
                key={total.currency}
                label={`Patrimonio (${total.currency})`}
                value={formatCurrency(total.totalValue, total.currency)}
                hint={
                  <span className="dash__stat-lines">
                    {/* Il numero grande resta l'investito, come è sempre stato; la
                        liquidità e il totale che ne risulta stanno sotto, in muted,
                        perché senza di loro «patrimonio» era metà del patrimonio. */}
                    <span>Liquidità: {formatCurrency(total.cashBalance, total.currency)}</span>
                    <span className="dash__stat-total">
                      Totale con liquidità: {formatCurrency(total.totalWithCash, total.currency)}
                    </span>
                    <span>
                      {total.portfolioCount} {total.portfolioCount === 1 ? 'portafoglio' : 'portafogli'}
                    </span>
                  </span>
                }
              />
            ))}
            <StatCard
              label="Portafogli attivi"
              value={formatNumber(summary.counts.activePortfolios)}
              hint={excludedHint(summary.counts)}
            />
            <StatCard
              label="Strumenti"
              value={formatNumber(summary.counts.instruments)}
              hint="Presenti nelle posizioni"
            />
            <StatCard
              label="Transazioni"
              value={formatNumber(summary.counts.transactions)}
              hint={`${summary.counts.imports} import eseguiti`}
            />
          </section>

          <section className="dash__grid">
            <Card title="Ripartizione per tipologia">
              <AllocationBars rows={typeRows(summary.allocationByType, summary.totalsByCurrency)} />
            </Card>

            <Card title="Ripartizione per piattaforma" subtitle="Titoli e liquidità sulla stessa barra">
              <AllocationBars rows={platformRows(summary.allocationByPlatform)} />
            </Card>
          </section>

          <Card title="Portafogli" subtitle="Titoli dell'ultimo snapshot disponibile, più liquidità" flush>
            {portfolios.length === 0 ? (
              <EmptyState title="Nessun portafoglio attivo" />
            ) : (
              <TableWrap>
                <table className="table table--compact">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th className="col--secondary">Piattaforma</th>
                      <th className="col--tertiary">Tipologia</th>
                      <th className="col--secondary">Ultimo snapshot</th>
                      <th className="numeric">Posizioni</th>
                      <th className="numeric col--tertiary">Movimenti</th>
                      <th className="numeric">Valore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolios.map((portfolio) => (
                      <tr key={portfolio.id}>
                        <td>{portfolio.name}</td>
                        <td className="col--secondary">{portfolio.platform.name}</td>
                        <td className="col--tertiary">
                          <Badge>
                            {labelFor(PORTFOLIO_TYPE_LABELS, portfolio.portfolioType)}
                          </Badge>
                        </td>
                        <td className="cell--minor col--secondary">{formatDate(portfolio.latestSnapshot?.snapshotDate)}</td>
                        <td className="numeric">
                          {portfolio.latestSnapshot
                            ? formatNumber(portfolio.latestSnapshot.holdingsCount)
                            : '—'}
                        </td>
                        <td className="numeric col--tertiary">{formatNumber(portfolio.transactionCount)}</td>
                        <td className="numeric">
                          <PortfolioValueCell
                            portfolio={portfolio}
                            max={maxPortfolioValue.get(portfolio.latestSnapshot?.currency ?? portfolio.baseCurrency) ?? 0}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </Card>

          {summary.topHoldings.length > 0 && (
            <Card title="Posizioni principali" subtitle="Per valore di mercato" flush>
              <TableWrap>
                <table className="table table--compact">
                  <thead>
                    <tr>
                      <th>Strumento</th>
                      <th className="col--secondary">Portafoglio</th>
                      <th className="numeric col--tertiary">Quantità</th>
                      <th className="numeric">Valore</th>
                      <th className="numeric">P&amp;L</th>
                      <th className="numeric">P&amp;L %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.topHoldings.map((holding) => {
                      const plSign = signOf(holding.unrealizedPl);
                      const plClass =
                        plSign === 'positive'
                          ? 'text-positive'
                          : plSign === 'negative'
                            ? 'text-negative'
                            : '';

                      return (
                        <tr key={`${holding.instrumentId}-${holding.portfolioName}`}>
                          <td>
                            <div className="dash__instrument">
                              <span>{holding.name}</span>
                              <span className="dash__instrument-meta">
                                {holding.symbol ?? holding.isin ?? '—'}
                              </span>
                            </div>
                          </td>
                          <td className="cell--minor col--secondary">{holding.portfolioName}</td>
                          <td className="numeric col--tertiary">{formatQuantity(holding.quantity)}</td>
                          <td className="numeric">
                            {formatCurrency(holding.marketValue, holding.currency)}
                          </td>
                          <td className={`numeric ${plClass}`}>
                            {formatCurrency(holding.unrealizedPl, holding.currency)}
                          </td>
                          <td className={`numeric ${plClass}`}>
                            {formatPercent(holding.unrealizedPlPct)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
            </Card>
          )}

          <section className="dash__grid">
            <Card title="Flussi di cassa" subtitle="Ricostruiti dalle transazioni">
              {summary.cashFlow.length === 0 ? (
                <p className="text-muted">Nessuna transazione registrata.</p>
              ) : (
                <ul className="dash__flows">
                  {summary.cashFlow.map((flow) => (
                    <li key={flow.currency} className="dash__flow">
                      <h3 className="dash__flow-title">{flow.currency}</h3>
                      <dl className="dash__flow-grid">
                        <dt>Versamenti</dt>
                        <dd className="numeric">{formatCurrency(flow.deposits, flow.currency)}</dd>
                        <dt>Prelievi</dt>
                        <dd className="numeric">
                          {formatCurrency(flow.withdrawals, flow.currency)}
                        </dd>
                        <dt>Dividendi</dt>
                        <dd className="numeric text-positive">
                          {formatCurrency(flow.dividends, flow.currency)}
                        </dd>
                        <dt>Interessi</dt>
                        <dd className="numeric text-positive">
                          {formatCurrency(flow.interest, flow.currency)}
                        </dd>
                        <dt>Commissioni</dt>
                        <dd className="numeric">{formatCurrency(flow.fees, flow.currency)}</dd>
                        <dt>Imposte</dt>
                        <dd className="numeric">{formatCurrency(flow.taxes, flow.currency)}</dd>
                      </dl>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Ultimi movimenti" flush>
              {summary.recentTransactions.length === 0 ? (
                <EmptyState title="Nessun movimento" description="Importa un CSV per popolarli." />
              ) : (
                <TableWrap>
                  <table className="table table--compact">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Strumento</th>
                        <th className="numeric">Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.recentTransactions.map((tx) => {
                        const sign = signOf(tx.amount);
                        return (
                          <tr key={tx.id}>
                            <td className="cell--minor">{formatDateTime(tx.txDate)}</td>
                            <td>
                              <Badge>{labelFor(TX_TYPE_LABELS, tx.txType)}</Badge>
                            </td>
                            <td>{tx.instrumentName ?? <span className="text-muted">—</span>}</td>
                            <td
                              className={`numeric ${
                                sign === 'positive'
                                  ? 'text-positive'
                                  : sign === 'negative'
                                    ? 'text-negative'
                                    : ''
                              }`}
                            >
                              {formatCurrency(tx.amount, tx.currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableWrap>
              )}
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
